import { ProviderType } from '../models/project-integration.model';
import { boardHost } from './issue-links';
import { providerLabel } from './provider-meta';

/** Meldungen fuer Absagen eines Trackers, die die Person selbst beheben kann (NEX-1353). */
export const TRACKER_ERROR_KEYS = {
  tokenInvalid: 'trackerError.tokenInvalid',
  forbidden: 'trackerError.forbidden',
  forbiddenRedmine: 'trackerError.forbiddenRedmine',
  issueInvalid: 'trackerError.issueInvalid',
  integrationInactive: 'trackerError.integrationInactive',
  toAccount: 'trackerError.toAccount',
} as const;

/** Was eine Ansicht statt ihrer allgemeinen Fehlermeldung zeigt. */
export interface TrackerError {
  key: string;
  params: Record<string, string>;
  /** Nur ein abgelehntes Token laesst sich im Konto beheben. */
  accountLink: boolean;
}

const TOKEN_INVALID: ReadonlyMap<string, ProviderType> = new Map([
  ['JIRA_TOKEN_INVALID', 'JIRA'],
  ['REDMINE_TOKEN_INVALID', 'REDMINE'],
]);

const ISSUE_INVALID: ReadonlyMap<string, ProviderType> = new Map([
  ['JIRA_ISSUE_INVALID', 'JIRA'],
  ['REDMINE_ISSUE_INVALID', 'REDMINE'],
]);

const INTEGRATION_INACTIVE: ReadonlySet<string> = new Set([
  'JIRA_INTEGRATION_INACTIVE',
  'REDMINE_INTEGRATION_INACTIVE',
]);

/**
 * Liest den {@code code} der Fehlerantwort des {@code ApiExceptionHandler}; den deutschen
 * {@code error}-Text wertet das Frontend bewusst nicht aus (Ausnahme: {@link mapBugCreateError}).
 * Ohne bekannten Code bleibt es bei der bisherigen Meldung der Ansicht ({@code null}) — auch
 * beim 412 eines fehlenden Tokens.
 */
export function mapTrackerError(error: unknown, boardUrl?: string | null): TrackerError | null {
  const code = codeOf(error);
  if (!code) return null;
  const provider = TOKEN_INVALID.get(code);
  if (provider) {
    return {
      key: TRACKER_ERROR_KEYS.tokenInvalid,
      params: { host: boardHost(boardUrl) || providerLabel(provider) },
      accountLink: true,
    };
  }
  if (code === 'JIRA_FORBIDDEN') {
    return { key: TRACKER_ERROR_KEYS.forbidden, params: {}, accountLink: false };
  }
  if (code === 'REDMINE_FORBIDDEN') {
    return { key: TRACKER_ERROR_KEYS.forbiddenRedmine, params: {}, accountLink: false };
  }
  if (INTEGRATION_INACTIVE.has(code)) {
    return { key: TRACKER_ERROR_KEYS.integrationInactive, params: {}, accountLink: false };
  }
  return null;
}

/**
 * Fuer die Bug-Anlage: Lehnt der Tracker den Vorgang fachlich ab ({@code *_ISSUE_INVALID}), steht
 * seine Begruendung im {@code error}-Text der Antwort (NEX-1357) — nur hier zeigt das Frontend
 * ihn. Ohne Begruendung bleibt es bei der allgemeinen Meldung der Ansicht.
 */
export function mapBugCreateError(error: unknown, boardUrl?: string | null): TrackerError | null {
  const known = mapTrackerError(error, boardUrl);
  if (known) return known;
  const code = codeOf(error);
  const provider = code ? ISSUE_INVALID.get(code) : undefined;
  const reason = reasonOf(error);
  if (!provider || !reason) return null;
  return {
    key: TRACKER_ERROR_KEYS.issueInvalid,
    params: { provider: providerLabel(provider), reason },
    accountLink: false,
  };
}

function reasonOf(error: unknown): string | null {
  const body = (error as { error?: unknown } | null | undefined)?.error;
  const reason = typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : null;
  return typeof reason === 'string' && reason.trim() ? reason.trim() : null;
}

function codeOf(error: unknown): string | null {
  const body = (error as { error?: unknown } | null | undefined)?.error;
  const code = typeof body === 'object' && body !== null ? (body as { code?: unknown }).code : null;
  return typeof code === 'string' ? code : null;
}
